/* eslint-disable @typescript-eslint/no-use-before-define */

import performanceNow from '../vendor/performanceNow';

/*
  This code is taken from
  https://github.com/GoogleChrome/web-vitals/blob/3f3338d994f182172d5b97b22a0fcce0c2846908/src/lib/polyfills/firstInputPolyfill.ts
*/

import { PerformanceEventTiming } from './performanceApi';

export type FirstInputPolyfillEntry = Omit<
  PerformanceEventTiming,
  'processingEnd' | 'toJSON'
>;

export interface FirstInputPolyfillCallback {
  (entry: FirstInputPolyfillEntry): void;
}

export type addOrRemoveEventListener =
  | typeof window.addEventListener
  | typeof window.removeEventListener;

let firstInputEvent: Event | null;
let firstInputDelay: number;
let firstInputTimeStamp: Date;
let callbacks: FirstInputPolyfillCallback[];

const listenerOpts: AddEventListenerOptions = { passive: true, capture: true };
const startTimeStamp: Date = new Date();

export const firstInputPolyfill = (
  onFirstInput: FirstInputPolyfillCallback
) => {
  callbacks.push(onFirstInput);
  reportFirstInputDelayIfRecordedAndValid();
};

export const resetFirstInputPolyfill = () => {
  callbacks = [];
  firstInputDelay = -1;
  firstInputEvent = null;
  eachEventType(window.addEventListener);
};

const recordFirstInputDelay = (delay: number, event: Event) => {
  if (!firstInputEvent) {
    firstInputEvent = event;
    firstInputDelay = delay;
    firstInputTimeStamp = new Date();

    eachEventType(window.removeEventListener);
    reportFirstInputDelayIfRecordedAndValid();
  }
};

const reportFirstInputDelayIfRecordedAndValid = () => {
  if (
    firstInputDelay >= 0 &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore (subtracting two dates always returns a number)
    firstInputDelay < firstInputTimeStamp - startTimeStamp
  ) {
    const entry = {
      entryType: 'first-input',
      name: firstInputEvent!.type,
      target: firstInputEvent!.target,
      cancelable: firstInputEvent!.cancelable,
      startTime: firstInputEvent!.timeStamp,
      processingStart: firstInputEvent!.timeStamp + firstInputDelay,
    } as unknown as FirstInputPolyfillEntry;
    callbacks.forEach((callback) => {
      callback(entry);
    });
    callbacks = [];
  }
};

const onInput = (event: Event) => {
  if (event.cancelable) {
    const isEpochTime = event.timeStamp > 1e12;
    const now = isEpochTime ? new Date() : performanceNow();

    const delay = (now as number) - event.timeStamp;

    if (event.type === 'pointerdown') {
      onPointerDown(delay, event);
    } else {
      recordFirstInputDelay(delay, event);
    }
  }
};

// Handles pointer down events, which are a special case, since we need to exclude scrolling and zooming.
const onPointerDown = (delay: number, event: Event) => {
  const onPointerUp = () => {
    recordFirstInputDelay(delay, event);
    removePointerEventListeners();
  };

  const onPointerCancel = () => {
    removePointerEventListeners();
  };

  const removePointerEventListeners = () => {
    window.removeEventListener('pointerup', onPointerUp, listenerOpts);
    window.removeEventListener('pointercancel', onPointerCancel, listenerOpts);
  };

  window.addEventListener('pointerup', onPointerUp, listenerOpts);
  window.addEventListener('pointercancel', onPointerCancel, listenerOpts);
};

const eachEventType = (callback: addOrRemoveEventListener) => {
  const eventTypes = ['mousedown', 'keydown', 'touchstart', 'pointerdown'];
  eventTypes.forEach((type) => callback(type, onInput, listenerOpts));
};
